import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchGuidedTutorialProgress, updateGuidedTutorialProgress } from "@/lib/shopApi";
import { useAuth } from "@/lib/state/authState";
import { useWorkspace } from "@/lib/state/workspaceState";
import { hasPermission } from "@/lib/permissions";
import { GuidedTutorialOverlay } from "./GuidedTutorialOverlay";
import { optimisticTutorialProgress } from "./tutorialProgress";

const TutorialContext = createContext(null);
const CHANNEL_NAME = "shopbook-web-tutorial";

function storageKey(session) {
  return `shopbook:web:tutorial:${session?.shopId || "shop"}:${session?.userId || "user"}`;
}

function readLocal(session) {
  try {
    return JSON.parse(window.localStorage.getItem(storageKey(session)) || "null") || {};
  } catch {
    return {};
  }
}

function writeLocal(session, value) {
  try {
    window.localStorage.setItem(storageKey(session), JSON.stringify(value));
  } catch {
    // Server progress remains authoritative if browser storage is unavailable.
  }
}

function stageForStep(step) {
  if (step === "inventory_update") return "inventoryRow";
  if (step === "transaction_history") return "history";
  if (step === "dashboard_overview") return "dashboard";
  return "search";
}

function stageMatchesStep(stage, step) {
  if (step === "first_sale") return ["search", "existing", "price", "add", "cart", "payment", "customer", "phone", "due", "amount", "complete"].includes(stage);
  if (step === "inventory_update") return ["inventoryRow", "cost", "stock", "saveProduct"].includes(stage);
  return stage === stageForStep(step);
}

export function TutorialProvider({ children }) {
  const { session } = useAuth();
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState(null);
  const [stage, setStage] = useState(null);
  const [focus, setFocus] = useState({ productId: "", saleId: "" });
  const [completionOpen, setCompletionOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const commandInFlight = useRef(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const persist = useCallback((next) => {
    if (!sessionRef.current) return;
    const local = { ...readLocal(sessionRef.current), ...next };
    writeLocal(sessionRef.current, local);
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage({ key: storageKey(sessionRef.current), value: local });
      channel.close();
    } catch {
      // The storage event still synchronizes browsers without BroadcastChannel.
    }
  }, []);

  const applyProgress = useCallback((next, local = {}) => {
    if (!next) return;
    setProgress(next);
    setStage(local.stage || stageForStep(next.currentStep));
    if (local.focus) setFocus(local.focus);
    persist({ progress: next, stage: local.stage || stageForStep(next.currentStep), focus: local.focus });
  }, [persist]);

  const flushPending = useCallback(async () => {
    const activeSession = sessionRef.current;
    if (!activeSession?.sessionId || commandInFlight.current || !navigator.onLine) return;
    const cached = readLocal(activeSession);
    const pending = Array.isArray(cached.pending) ? [...cached.pending] : [];
    if (!pending.length) return;
    commandInFlight.current = true;
    try {
      while (pending.length) {
        const command = pending[0];
        const next = await updateGuidedTutorialProgress(activeSession.sessionId, command);
        pending.shift();
        setProgress(next);
        writeLocal(activeSession, { ...readLocal(activeSession), pending, progress: next });
      }
    } catch {
      // Retry on reconnect, focus, or the next successful tutorial command.
    } finally {
      commandInFlight.current = false;
    }
  }, []);

  const command = useCallback(async (input, local = {}) => {
    const activeSession = sessionRef.current;
    if (!activeSession?.sessionId) return null;
    if (navigator.onLine && (readLocal(activeSession).pending || []).length) {
      await flushPending();
    }
    const optimistic = optimisticTutorialProgress(readLocal(activeSession).progress || progress, input);
    applyProgress(optimistic, local);
    const blockedPending = [...(readLocal(activeSession).pending || [])];
    if (blockedPending.length) {
      if (!blockedPending.some((item) => item.action === input.action && item.expectedStep === input.expectedStep)) blockedPending.push(input);
      writeLocal(activeSession, { ...readLocal(activeSession), pending: blockedPending, progress: optimistic, stage: local.stage || stage, focus: local.focus || focus });
      return optimistic;
    }
    try {
      const next = await updateGuidedTutorialProgress(activeSession.sessionId, input);
      applyProgress(next, local);
      void flushPending();
      return next;
    } catch {
      const cached = readLocal(activeSession);
      const pending = [...(cached.pending || [])];
      if (!pending.some((item) => item.action === input.action && item.expectedStep === input.expectedStep)) pending.push(input);
      writeLocal(activeSession, { ...cached, pending, progress: optimistic, stage: local.stage || stage, focus: local.focus || focus });
      return optimistic;
    }
  }, [applyProgress, flushPending, focus, progress, stage]);

  useEffect(() => {
    const activeSession = sessionRef.current;
    if (!activeSession?.sessionId) {
      setProgress(null);
      setStage(null);
      setReady(false);
      return undefined;
    }
    let active = true;
    const cached = readLocal(activeSession);
    if (cached.progress) {
      setProgress(cached.progress);
      setStage(stageMatchesStep(cached.stage, cached.progress.currentStep) ? cached.stage : stageForStep(cached.progress.currentStep));
      setFocus(cached.focus || { productId: "", saleId: "" });
    }
    fetchGuidedTutorialProgress(activeSession.sessionId)
      .then((next) => {
        if (!active) return;
        const latest = readLocal(activeSession);
        setProgress(next);
        setStage(next.status === "in_progress" && stageMatchesStep(latest.stage, next.currentStep) ? latest.stage : stageForStep(next.currentStep));
        setFocus(latest.focus || { productId: "", saleId: "" });
        writeLocal(activeSession, { ...latest, progress: next });
      })
      .catch(() => null)
      .finally(() => {
        if (active) setReady(true);
        void flushPending();
      });
    return () => { active = false; };
  }, [flushPending, session?.sessionId]);

  useEffect(() => {
    const sync = (value) => {
      if (!value) return;
      if (value.progress) setProgress(value.progress);
      if (value.stage) setStage(value.stage);
      if (value.focus) setFocus(value.focus);
    };
    const onStorage = (event) => {
      if (event.key !== storageKey(session)) return;
      try { sync(JSON.parse(event.newValue || "null")); } catch { /* Ignore corrupt external storage writes. */ }
    };
    let channel;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event) => {
        if (event.data?.key === storageKey(session)) sync(event.data.value);
      };
    } catch {
      channel = null;
    }
    window.addEventListener("storage", onStorage);
    window.addEventListener("online", flushPending);
    window.addEventListener("focus", flushPending);
    return () => {
      channel?.close();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("online", flushPending);
      window.removeEventListener("focus", flushPending);
    };
  }, [flushPending, session]);

  const eligible = Boolean(session && !workspace.loading && ["sell", "inventory", "history", "analytics"].every((permission) => hasPermission(session, permission)));

  useEffect(() => {
    if (!ready || !eligible || progress?.status !== "not_started") return;
    void command({ action: "start" }, { stage: "search" });
    navigate("/sell");
  }, [command, eligible, navigate, progress?.status, ready]);

  useEffect(() => {
    if (!eligible || progress?.status !== "in_progress") return;
    const destination = { first_sale: "/sell", inventory_update: "/inventory", transaction_history: "/history", dashboard_overview: "/dashboard" }[progress.currentStep];
    if (destination && !location.pathname.startsWith(destination)) navigate(destination);
  }, [eligible, location.pathname, navigate, progress?.currentStep, progress?.status]);

  const restart = useCallback(async () => {
    if (!eligible) return false;
    setCompletionOpen(false);
    setFocus({ productId: "", saleId: "" });
    await command({ action: "restart" }, { stage: "search", focus: { productId: "", saleId: "" } });
    navigate("/sell");
    return true;
  }, [command, eligible, navigate]);

  const setTutorialStage = useCallback((next) => {
    setStage(next);
    persist({ stage: next, focus });
  }, [focus, persist]);

  const saleCompleted = useCallback(async ({ productId, saleId }) => {
    if (progress?.status !== "in_progress" || progress.currentStep !== "first_sale") return;
    const nextFocus = { productId: productId || "", saleId: saleId || "" };
    setFocus(nextFocus);
    await command({ action: "advance", expectedStep: "first_sale" }, { stage: "inventoryRow", focus: nextFocus });
    navigate("/inventory");
  }, [command, navigate, progress?.currentStep, progress?.status]);

  const productSetupCompleted = useCallback(async (productId) => {
    if (progress?.status !== "in_progress" || progress.currentStep !== "inventory_update") return;
    const nextFocus = { ...focus, productId: productId || focus.productId };
    await command({ action: "advance", expectedStep: "inventory_update" }, { stage: "history", focus: nextFocus });
    navigate("/history");
  }, [command, focus, navigate, progress?.currentStep, progress?.status]);

  const advanceHistory = useCallback(async () => {
    await command({ action: "advance", expectedStep: "transaction_history" }, { stage: "dashboard" });
    navigate("/dashboard");
  }, [command, navigate]);

  const acknowledgeDashboard = useCallback(() => setCompletionOpen(true), []);
  const skip = useCallback(async () => {
    setCompletionOpen(false);
    await command({ action: "skip" });
  }, [command]);
  const finish = useCallback(async (goToTutorials = false) => {
    await command({ action: "complete" });
    setCompletionOpen(false);
    if (goToTutorials) navigate("/tutorials");
  }, [command, navigate]);

  const value = useMemo(() => ({
    acknowledgeDashboard,
    active: progress?.status === "in_progress" && eligible,
    advanceHistory,
    completionOpen,
    eligible,
    finish,
    focus,
    productSetupCompleted,
    progress,
    restart,
    saleCompleted,
    setStage: setTutorialStage,
    skip,
    stage,
  }), [acknowledgeDashboard, advanceHistory, completionOpen, eligible, finish, focus, productSetupCompleted, progress, restart, saleCompleted, setTutorialStage, skip, stage]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
      <GuidedTutorialOverlay />
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) throw new Error("useTutorial must be used inside TutorialProvider");
  return context;
}
